import type {
  AnalyticsOverview,
  CachSapSoSanh,
  ChuoiChiSo,
  MaChiSo,
  SoSanhKenh as SoSanhKenhDto,
  TheChiSo,
  TopVideosResponse,
  TrafficSourcesResponse,
} from '@qlk/shared';
import { MO_TA_CHI_SO } from '@qlk/shared';

import type { KyTrang } from '../../../../lib/kyTrang.js';
import { nhanSoKyTruoc } from '../../../../lib/kyTrang.js';
import { EmptyState, Nut, Skeleton } from '../../../../ui/index.js';
import { NguonLuuLuong } from '../NguonLuuLuong.js';
import { KhungChiSo } from '../hieuSuat/KhungChiSo.js';
import { BieuDoChiSo } from './BieuDoChiSo.js';
import { SoSanhKenh } from './SoSanhKenh.js';
import { VideoNoiBatGon } from './VideoNoiBatGon.js';

/**
 * Tab "Tổng quan" của trang Hiệu suất — **một chỉ số dẫn cả tab**.
 *
 * Tách khỏi `TrangPhanTich` khi trang có bốn tab (30/10/2026): trang giờ chỉ
 * còn việc của mình (tiêu đề, chip phạm vi, thanh tab, chọn tab nào gọi hook
 * nào), còn bố cục của từng tab nằm ở tab đó.
 *
 * Bấm một ô chỉ số thì biểu đồ bên dưới và khung "So sánh kênh" đều đổi theo.
 * Ba tab kia không có cơ chế đó — chúng có khối riêng của chúng — nên `?chi-so=`
 * chỉ có nghĩa ở đây.
 */

export interface TabTongQuanProps {
  the: TheChiSo[];
  chiSo: MaChiSo;
  onChonChiSo: (khoa: string) => void;
  kyTrang: KyTrang | null;
  khoangTruoc: { from: string; to: string } | undefined;
  chiSoQ: { data: AnalyticsOverview | undefined; isPending: boolean };
  chuoiQ: { data: ChuoiChiSo | undefined; isPending: boolean };
  soSanhQ: {
    data: SoSanhKenhDto | undefined;
    isPending: boolean;
    error: { message: string } | null;
  };
  nguonQ: { data: TrafficSourcesResponse | undefined; isPending: boolean };
  videoQ: { data: TopVideosResponse | undefined; isPending: boolean };
  sap: CachSapSoSanh;
  onDoiSap: (sap: CachSapSoSanh) => void;
  maKy: string;
  goiDuoc: boolean;
  xemTatCa: boolean;
  onXemTatCa: () => void;
  /** Kỳ ngoài mốc dữ liệu — hiện lời mời chọn kỳ khác thay cho dải số 0. */
  kyRong: boolean;
  loiChiSo: string | null;
  onThuLai: () => void;
}

export function TabTongQuan({
  the,
  chiSo,
  onChonChiSo,
  kyTrang,
  khoangTruoc,
  chiSoQ,
  chuoiQ,
  soSanhQ,
  nguonQ,
  videoQ,
  sap,
  onDoiSap,
  maKy,
  goiDuoc,
  xemTatCa,
  onXemTatCa,
  kyRong,
  loiChiSo,
  onThuLai,
}: TabTongQuanProps) {
  return (
    <>
      {loiChiSo !== null ? (
        <div className="mb-6 rounded-the border border-vien bg-be-mat">
          <EmptyState
            loi
            title="Không tải được số liệu hiệu suất"
            description={loiChiSo}
            action={
              <Nut kieu="phu" onClick={onThuLai}>
                Thử lại
              </Nut>
            }
          />
        </div>
      ) : kyRong ? (
        <div className="mb-6 rounded-the border border-vien bg-be-mat">
          <EmptyState
            title="Không có số liệu trong kỳ này"
            description="Chọn một kỳ khác ở ô chọn kỳ phía trên."
          />
        </div>
      ) : (
        <section className="mb-4" data-khoi="chi-so">
          {/*
            Tiêu đề khối **ghi rõ kỳ**: tám ô mà mỗi ô gắn thêm "· Tháng 9/2026"
            thì nhãn dài hơn con số, nhưng một dải số không nói kỳ nào là một
            dải số không ai dám mang đi so (`docs/ui.md` mục 3). Nói một lần cho
            cả khối, ngay trên hàng ô.
          */}
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-tieu-de-khoi font-semibold text-chu">
              Chỉ số · {kyTrang?.nhan ?? ''}
            </h2>
            <p className="text-phu text-chu-mo">
              Bấm một ô để biểu đồ và bảng so sánh đổi theo · {nhanSoKyTruoc(kyTrang, khoangTruoc)}
            </p>
          </div>

          {chiSoQ.isPending ? (
            <div className="space-y-px">
              <Skeleton className="h-[216px] w-full rounded-t-the" />
              <Skeleton className="h-[292px] w-full rounded-b-the" />
            </div>
          ) : (
            <KhungChiSo
              the={the}
              mocXuatBan={[]}
              mau="luot-xem"
              nhanKy={kyTrang?.nhan ?? ''}
              /* Không truyền `nhanKyTruoc`: ô kiểu `o` in "so kỳ trước" gọn,
                 còn khoảng ngày đã nằm ở dòng phụ dưới tiêu đề trang. */
              dangTai={false}
              kieu="o"
              chon={chiSo}
              onChon={onChonChiSo}
              /* Biểu đồ của tab này là một request riêng theo chỉ số đang chọn,
                 nên thay chỗ biểu đồ mặc định của khung. */
              bieuDo={
                <BieuDoChiSo
                  du={chuoiQ.data}
                  nhan={MO_TA_CHI_SO[chiSo].nhan}
                  dangTai={chuoiQ.isPending}
                  nhanKy={kyTrang?.nhan ?? ''}
                />
              }
            />
          )}
        </section>
      )}

      {/*
        Hàng dưới: so sánh kênh (≈3/5) và hai khối nguồn (≈2/5). Xếp chồng dưới
        1100px bề ngang **vùng nội dung** — xem `docs/ui.md` mục 9.
      */}
      <div className="grid grid-cols-1 gap-4 @min-[1100px]/noi-dung:grid-cols-[3fr_2fr]">
        <SoSanhKenh
          du={soSanhQ.data}
          nhanChiSo={MO_TA_CHI_SO[chiSo].nhan}
          dangTai={soSanhQ.isPending && goiDuoc}
          loi={soSanhQ.error ? soSanhQ.error.message : null}
          sap={sap}
          onDoiSap={onDoiSap}
          maKy={maKy}
          xemTatCa={xemTatCa}
          onXemTatCa={onXemTatCa}
        />

        <div className="flex flex-col gap-4">
          <NguonLuuLuong duLieu={nguonQ.data} dangTai={nguonQ.isPending} />
          <VideoNoiBatGon du={videoQ.data} dangTai={videoQ.isPending} />
        </div>
      </div>
    </>
  );
}
